import { getUserProjectIds } from '@resistapp/common/access';
import React, { createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { PathParams, usePathParam } from '../hooks/use-path-params';
import { useUrlLengthWarning } from '../hooks/use-url-length-warning';
import { getBestPageForProject } from '../utils/project-page-access';
import { useUser } from './use-user-context';

type UrlContextType = {
  projectId: {
    current: number | undefined;
    viable: number | undefined;
  };
  page: {
    current: string;
    viable: string;
  };
  query: string;
};

const UrlContext = createContext<UrlContextType | undefined>(undefined);

export const UrlProvider = ({ children }: { children: React.ReactNode }) => {
  const projectId = usePathParam(PathParams.projectId);
  const location = useLocation();
  const { user } = useUser();

  // Monitor URL length and warn if it gets too long
  useUrlLengthWarning();

  const accessibleProjectIds = getUserProjectIds(user);
  const latestProjectId = accessibleProjectIds[0];
  const viableProjectId = projectId || (accessibleProjectIds.length ? latestProjectId : undefined);
  const viablePage = getBestPageForProject(user, viableProjectId);
  const currentPage = location.pathname.split('/')[1];

  return (
    <UrlContext.Provider
      value={{
        projectId: {
          current: projectId,
          viable: viableProjectId,
        },
        page: {
          current: currentPage,
          viable: viablePage,
        },
        query: location.search,
      }}
    >
      {children}
    </UrlContext.Provider>
  );
};

export const useUrlContext = () => {
  const context = useContext(UrlContext);

  if (!context) {
    throw new Error('useUrlContext must be used within a UrlProvider');
  }

  return context;
};
